// Legacy CakeCRM stored amenities in properties.services as slug-style keys
// ("casa_club_piscina"); the new CRM property form writes canonical Spanish
// names from the amenities table ("Casa club: Piscina"). Both forms coexist in
// production, so any code that reads `services` for matching has to normalize.
// Use the canonical name for display and the slug for `properties.services`
// containment checks against legacy rows.
export function slugifyAmenity(name: string): string {
  return String(name)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

// The property-form amenity picker offers canonical catalog names but reads the
// property's `services`, which for every migrated row holds legacy slugs. An
// exact-name check left those chips unselected — invisible to the agent, yet
// re-saved verbatim and rendered on the vitrina, which read as "amenities I
// never assigned". Both helpers compare by slug so legacy tokens show as
// selected and can be unchecked; selecting writes the canonical name, so a row
// heals to canonical form on its next save.
export function isAmenitySelected(selected: unknown[], name: string): boolean {
  const slug = slugifyAmenity(name);
  return selected.some((a) => slugifyAmenity(String(a)) === slug);
}

export function toggleAmenity(selected: string[], name: string): string[] {
  const slug = slugifyAmenity(name);
  return isAmenitySelected(selected, name)
    ? selected.filter((a) => slugifyAmenity(String(a)) !== slug)
    : [...selected, name];
}

// True when a property's `services` (JSONB array of amenity names/slugs) contains
// EVERY requested canonical amenity name. Matches either the exact name or its
// slug so it works across the mixed name/slug forms that coexist in production.
// Empty `names` → vacuously true (no amenity constraint). Shared with the
// Intercambio amenities filter (applyExchangeFilters); mirrors PropiedadesView.
export function servicesHaveAllAmenities(services: unknown, names: string[]): boolean {
  if (!names.length) return true;
  const present = new Set(Array.isArray(services) ? services.map((s) => String(s)) : []);
  return names.every((name) => present.has(name) || present.has(slugifyAmenity(name)));
}
